import { motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowRight,
  Award,
  Building,
  CheckCircle2,
  Clock,
  FileCheck,
  Flame,
  GraduationCap,
  Zap,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { CERTIFICATION_INFO, COURSES, PROJECT_TYPES } from '../../data/content';
import { prefillServiceAndScroll } from '../../lib/scroll';
import Reveal from './Reveal';

const ICONS = { Building, Flame, FileCheck, Zap };

const Services = () => (
  <section id="servicos" data-testid="services-section" className="relative py-28 bg-[#061226] overflow-hidden">
    <div className="absolute bottom-[-20%] left-[-10%] w-[36rem] h-[36rem] spotlight-yellow opacity-40 pointer-events-none" />
    <div className="container mx-auto px-4 relative z-10">
      <Reveal className="text-center mb-14">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-300 mb-4">
          Projetos & Treinamentos
        </p>
        <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white">
          Nossos Serviços
        </h2>
        <p className="mt-4 text-slate-400 max-w-xl mx-auto text-sm sm:text-base">
          Engenharia de segurança e formação de brigadas com certificação reconhecida.
        </p>
      </Reveal>

      <Tabs defaultValue="projetos" className="max-w-5xl mx-auto">
        <TabsList className="grid grid-cols-2 w-full max-w-md mx-auto mb-12 rounded-full bg-white/5 border border-white/10 p-1 h-auto">
          <TabsTrigger
            value="projetos"
            data-testid="tab-projetos"
            className="rounded-full py-3 font-display font-bold uppercase tracking-wide text-sm text-slate-300 data-[state=active]:bg-amber-400 data-[state=active]:text-[#0A2847] transition-all"
          >
            <Building className="w-4 h-4 mr-2" />
            Projetos
          </TabsTrigger>
          <TabsTrigger
            value="treinamentos"
            data-testid="tab-treinamentos"
            className="rounded-full py-3 font-display font-bold uppercase tracking-wide text-sm text-slate-300 data-[state=active]:bg-amber-400 data-[state=active]:text-[#0A2847] transition-all"
          >
            <GraduationCap className="w-4 h-4 mr-2" />
            Treinamentos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="projetos">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="grid md:grid-cols-2 gap-5"
          >
            {PROJECT_TYPES.map((project) => {
              const Icon = ICONS[project.icon] || Building;
              return (
                <div
                  key={project.id}
                  data-testid={`project-card-${project.id}`}
                  className="group rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-7 hover:border-amber-400/40 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(252,211,77,0.08)] transition-all duration-500"
                >
                  <div className="w-12 h-12 rounded-xl bg-amber-400/10 border border-amber-400/20 flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-amber-400" />
                  </div>
                  <h3 className="font-display font-bold uppercase text-lg text-white tracking-tight">
                    {project.name}
                  </h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{project.description}</p>
                  <button
                    data-testid={`project-cta-${project.id}`}
                    onClick={() => prefillServiceAndScroll(project.name)}
                    className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-300 hover:text-amber-200 transition-colors"
                  >
                    Solicitar Projeto
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              );
            })}
          </motion.div>
        </TabsContent>

        <TabsContent value="treinamentos">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="grid md:grid-cols-2 gap-5"
          >
            {COURSES.map((course) => (
              <div
                key={course.id}
                data-testid={`course-card-${course.id}`}
                className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm overflow-hidden hover:border-amber-400/40 hover:-translate-y-1 transition-all duration-500"
              >
                <div className="flex items-center justify-between px-7 py-4 bg-[#0A2847]/70 border-b border-white/10">
                  <h3 className="font-display font-bold uppercase text-lg text-white tracking-tight">
                    {course.level}
                  </h3>
                  <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-300">
                    <Clock className="w-4 h-4" />
                    {course.duration}
                  </span>
                </div>
                <div className="p-7">
                  <p className="text-sm text-slate-400 leading-relaxed">{course.description}</p>
                  <ul className="mt-5 space-y-2">
                    {course.topics.map((topic) => (
                      <li key={topic} className="flex items-start gap-2 text-sm text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                        {topic}
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`course-cta-${course.id}`}
                    onClick={() => prefillServiceAndScroll(`Treinamento ${course.level}`)}
                    className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-300 hover:text-amber-200 transition-colors"
                  >
                    Quero me inscrever
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </motion.div>

          <div
            data-testid="certification-panel"
            className="mt-10 rounded-3xl border border-amber-400/20 bg-gradient-to-br from-[#0A2847] to-[#061226] p-8 md:p-10"
          >
            <div className="text-center mb-8">
              <Award className="w-14 h-14 text-amber-400 mx-auto mb-4" />
              <h3 className="font-display font-extrabold uppercase text-2xl text-white tracking-tight">
                Certificação Profissional
              </h3>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
                <h4 className="font-mono text-xs uppercase tracking-[0.25em] text-amber-300 mb-4">
                  Informações do certificado
                </h4>
                <p className="flex items-center gap-2 text-sm text-slate-200 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0" />
                  Validade: {CERTIFICATION_INFO.validity}
                </p>
                <p className="flex items-start gap-2 text-sm text-slate-200">
                  <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                  {CERTIFICATION_INFO.requirements}
                </p>
              </div>
              <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
                <h4 className="font-mono text-xs uppercase tracking-[0.25em] text-amber-300 mb-4">
                  Benefícios
                </h4>
                <ul className="space-y-2">
                  {CERTIFICATION_INFO.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-start gap-2 text-sm text-slate-200">
                      <CheckCircle2 className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  </section>
);

export default Services;
